<template>
  <div class="desktop-confirm-section">
    <div class="desktop-confirm-header">
      <h3 class="desktop-confirm-title">
        {{ $t('detail.confirmEntries') }}
      </h3>
    </div>
    
    <div
      v-if="lines.length === 0"
      class="desktop-no-entries"
    >
      {{ $t('detail.noEntriesSelected') }}
    </div>
    
    <div
      v-else
      class="desktop-lines-section"
    >
      <div class="desktop-lines-header">
        <span>{{ $t('detail.selectedLines', { count: lines.length }) }}</span>
        <button
          class="desktop-clear-btn"
          @click="clearAll"
        >
          {{ $t('common.clearAll') }}
        </button>
      </div>
      
      <div class="desktop-lines-list">
        <div
          v-for="(line, index) in lines"
          :key="index"
          class="desktop-line-item"
        >
          <div class="desktop-line-content">
            <div class="desktop-line-balls">
              <div
                v-for="number in line.numbers"
                :key="number"
                class="desktop-line-ball"
              >
                {{ number }}
              </div>
              <div
                v-if="getColorComboLineBonusLabel(line)"
                class="desktop-line-color-bonus"
              >
                <span class="desktop-line-bonus-separator">+</span>
                <span class="desktop-line-color-bonus-text">{{ getColorComboLineBonusLabel(line) }}</span>
              </div>
              <div
                v-else-if="line.bonus"
                class="desktop-line-bonus"
              >
                <span class="desktop-line-bonus-separator">+</span>
                <div class="desktop-line-bonus-ball">
                  {{ line.bonus }}
                </div>
              </div>
            </div>
          </div>
          <button
            class="desktop-remove-line-btn"
            @click="removeLine(index)"
          >
            ×
          </button>
        </div>
      </div>

      <button
        class="desktop-add-to-betslip-btn"
        :disabled="lines.length === 0 || isProcessing"
        @click="addToBetslip"
      >
        {{ $t('detail.addToBetslip') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { withDefaults, defineOptions } from 'vue';
import { getColorComboLineBonusLabel } from '@/constants/standardBetPlusColorBonus';
import type { BallColorSlug } from '@/constants/sameColorBalls';

defineOptions({
  name: 'DesktopConfirmSection',
});

interface Line {
  numbers: number[];
  bonus?: number;
  bonusColor?: BallColorSlug;
  numBets: number;
  picks: number;
  market?: {
    label?: string;
    odds?: number;
    market?: {
      slug?: string;
    };
    marketGroup?: {
      group_id: number;
      name: string;
      slug: string;
    };
    market_slug?: string;
  };
}

interface Props {
  lines: Line[];
  currentGameSlug?: string;
  selectedRelatedGames?: string[];
  isProcessing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  lines: () => [],
  currentGameSlug: '',
  selectedRelatedGames: () => [],
  isProcessing: false,
});

const emit = defineEmits([
  'clear-all',
  'remove-line',
  'add-to-betslip',
  'update:selectedRelatedGames',
]);

const removeLine = (index: number) => {
  emit('remove-line', index);
};

const clearAll = () => {
  emit('clear-all');
};

const addToBetslip = () => {
  emit('add-to-betslip', props.lines);
};
</script>

<style scoped>
.desktop-confirm-section {
  background: var(--colour-surface-cont-lowest);
  border-radius: var(--detail-panel-radius, 4px);
  margin-bottom: 12px;
  overflow: hidden;
  border: var(--detail-panel-border-width, 1px) solid var(--colour-surface-cont);
}

.desktop-confirm-header {
  padding: 12px 16px;
  background: var(--surface-detail-header);
  border-bottom: 1px solid var(--border-detail-panel);
}

.desktop-confirm-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-detail-header);
  margin: 0;
  text-transform: none;
}

.desktop-no-entries {
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-detail-row-muted);
  font-style: italic;
  background: var(--surface-detail-panel);
}

.desktop-lines-section {
  padding: 16px;
  background: var(--surface-detail-panel);
}

.desktop-lines-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.desktop-lines-header span {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-detail-body);
}

.desktop-clear-btn {
  background: none;
  border: none;
  color: var(--text-detail-body);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.desktop-clear-btn:hover {
  text-decoration: underline;
}

.desktop-lines-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.desktop-line-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--surface-detail-muted);
  border: 1px solid var(--border-detail-card);
  border-radius: 8px;
}

.desktop-line-content {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  margin-right: 12px;
}

.desktop-line-balls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.desktop-line-ball {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--surface-detail-card);
  color: var(--text-detail-card);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  border: 1.5px solid var(--colour-base-text-colour);
  flex-shrink: 0;
}

.desktop-line-bonus {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
}

.desktop-line-bonus-separator {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-detail-body);
}

.desktop-line-bonus-ball {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--surface-detail-card);
  color: var(--colour-orange-text-colour);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  border: 1.5px solid var(--colour-orange-button);
  flex-shrink: 0;
}

.desktop-line-color-bonus {
  display: flex;
  align-items: center;
  gap: 6px;
}

.desktop-line-color-bonus-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-detail-body);
  white-space: nowrap;
}

.desktop-remove-line-btn {
  background: none;
  border: none;
  color: var(--text-detail-body);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}

.desktop-add-to-betslip-btn {
  width: 100%;
  padding: 16px;
  background: var(--detail-confirm-action-bg, var(--bg-primary-button, var(--colour-orange-button)));
  color: var(--detail-confirm-action-color, var(--bg-primary-button-color, var(--text-button)));
  border: 1px solid var(--detail-confirm-action-border, var(--bg-primary-button-border, var(--colour-orange-button)));
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  text-transform: none;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.desktop-add-to-betslip-btn:hover:not(:disabled) {
  background: var(--detail-confirm-action-bg-hover, var(--bg-primary-button-hover, var(--bg-button-hover)));
  border-color: var(--detail-confirm-action-border-hover, var(--detail-confirm-action-bg-hover, var(--bg-primary-button-hover)));
}

.desktop-add-to-betslip-btn:disabled {
  background: var(--surface-detail-header);
  color: var(--text-detail-header-muted);
  border-color: var(--border-detail-panel);
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
